// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI XR Studio — a shot as the Studio panel saves it: an ordinary AYNI
 * Khipu (a `story`), so it is kept, listed and opened like any other, with
 * the timeline in its root `x-ayni-shot` (`shot.ts`). The first step holds
 * the dataset, the first keyframe's view and date, so any Khipu player
 * opens it on the right picture. The dataset is named by its id in this app
 * (`app:<id>`, as meeting recordings do), which `resolveDatasetKey` loads.
 *
 * Version 2 (`buildFilmKhipu`, Studio motion plan): the draft *is* a Khipu,
 * its steps the stops (each with its hold, transition and effects, Khipu
 * 1.1); the extension keeps only the output, the brand and the finishing.
 */

import { newKhipuId, type Khipu } from '../khipu'
import { KHIPU_SCHEMA_VERSION } from '../khipu'
import { pruneDatasets } from '../khipuEdit'
import { parseShot, SHOT_EXTENSION, type Shot, type ShotCaption, type ShotFinish, type ShotKeyframe, type ShotOutput } from './shot'
import type { BrandContent } from './brandKit'

export interface ShotDraft {
  title: string
  datasetId: string
  datasetTitle: string
  output: ShotOutput
  keyframes: ShotKeyframe[]
  captions: ShotCaption[]
  /** The chosen brand kit, copied into the shot; null for none. */
  brand?: { id: string } & BrandContent | null
}

/** The keyframes in time order, each inside the shot's length. */
export function orderedKeyframes(keyframes: readonly ShotKeyframe[], seconds: number): ShotKeyframe[] {
  return [...keyframes]
    .map((k) => ({ ...k, t: Math.min(Math.max(0, k.t), seconds) }))
    .sort((a, b) => a.t - b.t)
    .filter((k, i, all) => i === 0 || k.t > all[i - 1].t)
}

/** Build the shot Khipu, or null when the draft is not yet a usable shot. */
export function buildShotKhipu(draft: ShotDraft, nowIso: string, id: string = newKhipuId()): { khipu: Record<string, unknown>; shot: Shot } | null {
  const keyframes = orderedKeyframes(draft.keyframes, draft.output.seconds)
  if (!keyframes.length || !draft.datasetId) return null
  const first = keyframes[0]
  const khipu: Record<string, unknown> = {
    format: 'ayni-khipu',
    schemaVersion: KHIPU_SCHEMA_VERSION,
    id,
    kind: 'story',
    title: { en: draft.title.trim() || draft.datasetTitle },
    created: nowIso,
    privacy: { mode: 'anonymous' },
    datasets: [{ key: 'd1', ref: `app:${draft.datasetId}`, appId: draft.datasetId, title: { en: draft.datasetTitle } }],
    path: { steps: [{ id: 's1', n: 1, dataset: 'd1', view: first.view, time: { at: first.at } }] },
    [SHOT_EXTENSION]: {
      version: 1,
      output: draft.output,
      keyframes,
      dateMode: 'follow',
      borders: false,
      brandKit: draft.brand?.id ?? null,
      ...(draft.brand ? { brand: { logo: draft.brand.logo, lowerThird: draft.brand.lowerThird, accent: draft.brand.accent } } : {}),
      captions: draft.captions.filter((c) => c.label.trim() && c.t1 > c.t0),
    },
  }
  const shot = parseShot(khipu)
  return shot ? { khipu, shot } : null
}

/** The Studio's draft of a version 2 shot: a Khipu whose steps are the stops. */
export interface FilmDraft {
  title: string
  khipu: Khipu
  output: ShotOutput
  brand?: { id: string } & BrandContent | null
  finish: ShotFinish
}

/** Build the version 2 shot Khipu, or null while the draft is not a usable shot (no stop with a camera, too long…). */
export function buildFilmKhipu(draft: FilmDraft, nowIso: string): { khipu: Record<string, unknown>; shot: Shot } | null {
  const lang = draft.khipu.languages?.[0] ?? 'en'
  const title = draft.title.trim()
  const { seconds: _seconds, ...output } = draft.output
  const khipu: Record<string, unknown> = {
    ...pruneDatasets(draft.khipu),
    schemaVersion: KHIPU_SCHEMA_VERSION,
    ...(title ? { title: { ...draft.khipu.title, [lang]: title } } : {}),
    modified: nowIso.replace(/\.\d{3}Z$/, 'Z'),
    [SHOT_EXTENSION]: {
      version: 2,
      output,
      borders: false,
      brandKit: draft.brand?.id ?? null,
      ...(draft.brand ? { brand: { logo: draft.brand.logo, lowerThird: draft.brand.lowerThird, accent: draft.brand.accent } } : {}),
      finish: draft.finish,
    },
  }
  const shot = parseShot(khipu)
  return shot ? { khipu, shot } : null
}
